










































import {
  Component,
  Vue as VueDecorator,
Watch,
} from "vue-property-decorator";
import SearchFieldComponent from "./SearchField.vue";
import { applyExclusiveOptions } from "@/utils/MultiSelectionExclusivity";

/** Show the search field only once the list is long enough to warrant it. */
const SEARCH_THRESHOLD = 8;

interface Args<T> {
  options: Array<T>;
  current: T[];
  label: string ;
  formatter: (toFormat: T) => string;

  showSelectNothingOption?: boolean;
  hyDryClientConfig?: boolean;
  disabledOptions?: T[];
  /** Options that exclude each other: checking one of them unchecks the others. */
  exclusiveOptions?: T[];
}

@Component({
  components: {
    "search-field": SearchFieldComponent,
  },
})
export default class MultiSelectionDialogComponent<T> extends VueDecorator {
  isPickerOpen = false;

  options: Array<T> = [];
  current: T[] = [];
  hyDryClientConfig = false;

  label = "";
  showSelectNothingOption = false;

  formatter: (toFormat: T) => string = () => "";

  searchQuery = "";

  resolve?: (value: T[]) => void = undefined;
  reject?: (reason: string) => void = undefined;

  get showSearch() {
    return this.options.length > SEARCH_THRESHOLD;
  }

  /**
   * Options filtered by the search query, each paired with its original index
   * so checkbox toggling keeps working against the unfiltered `selection` array.
   */
  get filteredOptions() {
    const entries = this.options.map((item, index) => ({ item, index }));
    const query = this.searchQuery.toLowerCase().trim();

    if (query === "") {
      return entries;
    }

    return entries.filter(
      (entry) => this.getString(entry.item).toLowerCase().indexOf(query) >= 0
    );
  }

  onSearchQueryChanged(searchQuery: string) {
    this.searchQuery = searchQuery;
  }

  @Watch("selectedOptions")
  onSelectedOptionsChanged() {
    if (this.hyDryClientConfig) {
      if (this.selectedOptions.length === 1) {
        this.disabledOptions.push(this.selectedOptions[0]);
      }
      else if (this.selectedOptions.length === 0) {
        // Default selection for drying program+
        this.selectedOptions.push(this.options[0]);
      }
      else {
        if (this.disabledOptions.length !== 0) {
          this.disabledOptions.pop();
        }
      }
    }
  }

  async show(
    args: Args<T>,
  ) {
    this.options = args.options;
    this.current = args.current;
    this.formatter = args.formatter;
    this.label = args.label;
    this.showSelectNothingOption = args.showSelectNothingOption ?? true;
    this.hyDryClientConfig = args.hyDryClientConfig ?? false;
    this.selection = this.options.map(option => this.current.includes(option));
    this.disabledOptions = args.disabledOptions ?? [];
    this.exclusiveOptions = args.exclusiveOptions ?? [];
    this.searchQuery = "";

    this.isPickerOpen = true;


    return new Promise<T[]>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  errors!: Array<string>;

  selection: boolean[] = [];
  initialSelection: boolean[] = [];
  disabledOptions: T[] = [];
  exclusiveOptions: T[] = [];

  isItemChecked(position: number) {
    return this.selection[position];
  }

  /** An option that cannot be toggled: greyed out and not a click target. */
  isOptionDisabled(item: T) {
    return this.disabledOptions.includes(item);
  }

  get selectedOptions() {
    const result: T[] = [];

    this.selection.forEach((value, index) => {
      if (value) {
        result.push(this.options[index]);
      }
    });

    return result;
  }

  getString(item: T) {
    return this.formatter(item);
  }

  onBack() {
    this.isPickerOpen = false;
  }

  onSelectNothing() {
    this.isPickerOpen = false;
    this.selection = this.selection.map(() => false);
    this.resolve && this.resolve(this.selectedOptions);
  }

  onToggleSelection(position: number) {
    const selectedOption = this.selectedOptions[0] as unknown;

    if (
      !(this.hyDryClientConfig &&
      this.selectedOptions.length === 1 &&
      selectedOption === position) &&
      !this.disabledOptions.includes(this.options[position])
    ) {
      const update = [...this.selection];
      update[position] = !update[position];
      this.selection = applyExclusiveOptions(
        this.options,
        update,
        position,
        this.exclusiveOptions
      );
    }
    
  }

  onConfirm() {
    this.isPickerOpen = false;
    this.resolve && this.resolve(this.selectedOptions);
  }
}
